import Trade from '@/pages/Trades/Trade';
import apiBaseUrl from '../ApiBaseUrl';
var token = localStorage.getItem('authToken')?.toString();


const handleResponse = async (response: Response) => {
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error fetching data: ${response.statusText} - ${errorText}`);
    }
    return await response.json();
};

// Login API
export const login = async (model: { userName: string; password: string }) => {
    try {
        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Authenticate/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(model),
        });

        if (!response.ok) {
            const errorText = await response.text();
            const errorMessage = `Error ${response.status}: ${errorText || response.statusText}`;
            console.error('Response not OK:', response.statusText, errorText);
            throw new Error(errorMessage);
        } else {
            const data = await response.json();
            console.log('Login successful:', data);

            // Save token and expiration time
            localStorage.setItem('authToken', data.token);
            localStorage.setItem('tokenExpiration', data.expiration);
            localStorage.setItem('userName', data.userName);
            localStorage.setItem('userId', data.userId);
            return data;
        }
    } catch (error) {
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
            console.error('Network error or API endpoint is unreachable:', error);
            throw new Error('Unable to connect to the server. Please try again later.');
        } else {
            console.error('Error during login:', error);
            throw error;
        }
    }
};


// Register API
export const register = async (model: {
    Email: string;
    Password: string;
    ConfirmPassword: string;
    PhoneNumber: string;
    FirstName: string;
    LastName: string;
    CountryId: number;
}) => {
    try {
        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Authenticate/Register`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(model),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Response not OK:', response.statusText, errorText);
            throw new Error(`Error registering user: ${response.statusText}`);
        } else {
            const data = await handleResponse(response);
            console.log('Registration successful:', data);
            return data;
        }
    } catch (error) {
        console.error('Error during registration:', error);
        throw error;
    }
};

// Forgot Password API
export const forgotPassword = async (email: string) => {
    try {
        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Authenticate/ForgotPassword`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ Email: email }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Response not OK:', response.statusText, errorText);
            throw new Error(`Error sending forgot password email: ${response.statusText}`);
        } else {
            console.log('Forgot password email sent successfully');
            return true;
        }
    } catch (error) {
        console.error('Error during forgot password:', error);
        throw error;
    }
};

// Reset Password API
export const resetPassword = async (model: { Email: string; Token: string; NewPassword: string }) => {
    try {
        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Authenticate/ResetPassword`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(model),
        });

        if (!response.ok) {
            const errorText = await response.text();
            const errorMessage = `Error ${response.status}: ${errorText || response.statusText}`;
            console.error('Response not OK:', response.statusText, errorText);
            throw new Error(errorMessage);
        } else {
            console.log('Password reset successfully');
            return true;
        }
    } catch (error) {
        console.error('Error during reset password:', error);
        throw error;
    }
};

// Change Password API
export const changePassword = async (model: { OldPassword: string; NewPassword: string }) => {
    const authToken = localStorage.getItem('authToken'); // Get the token from local storage
    if (!authToken) {
        throw new Error('No authentication token found');
    }

    try {
        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Authenticate/ChangePassword`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`, // Include the token in the header
            },
            body: JSON.stringify(model),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Response not OK:', response.statusText, errorText);
            throw new Error(`Error changing password: ${errorText || response.statusText}`);
        } else {
            console.log('Password changed successfully');
            return true;
        }
    } catch (error) {
        console.error('Error during change password:', error);
        throw error;
    }
};

// api.ts
export const getCountryList = async (countryId: number) => {
    try {
        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Country/GetAllCountries?countryId=${countryId}`);
        if (!response.ok) {
            console.error('Response not OK:', response.statusText);
            return [];
        }
        return await response.json(); // Parse JSON
    } catch (error) {
        console.error('Error fetching data:', error);
        return []; // Return empty array on error
    }
};


// api/users.ts
export interface GetAllUsersParams {
    IsActive?: boolean;
    CountryId?: string;
    IsEmailConfirmed?: boolean;
    Role?: string;
    SearchTerm?: string;
    pageNumber?: number;
    pageSize?: number;
}

export interface UserResponse {
    id: string;
    customUserId: string;
    userName: string;
    email: string;
    phoneNumber?: string;
    emailConfirmed: boolean;
    firstName: string;
    lastName: string;
    fullName: string;
    creationDate: string;
    lockoutEnd?: string;
    isAlwaysWin: boolean;
    countryName?: string;
    roles?: string;
    avaliableBalance: number;
    lockingBalance: number;
    lastModify?: string;
    ipAddress?: string;
    browserInformation?: string;
    activeBalance: boolean;
}

export interface GetAllUsersResponse {
    totalUsers: number;
    pageNumber: number;
    pageSize: number;
    users: UserResponse[];
}

// api/users.ts
export const getAllUsers = async (params: GetAllUsersParams): Promise<GetAllUsersResponse> => {
    try {
        const queryString = new URLSearchParams({
            IsActive: params.IsActive?.toString() || 'true',
            CountryId: params.CountryId || '',
            IsEmailConfirmed: params.IsEmailConfirmed?.toString() || 'false',
            Role: params.Role || '',
            SearchTerm: params.SearchTerm || '',
            pageNumber: params.pageNumber?.toString() || '1',
            pageSize: params.pageSize?.toString() || '10',
        }).toString();

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Authenticate/GetAllUsers?${queryString}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            console.error('Response not OK:', response.statusText);
            const errorBody = await response.text();
            console.error('Error response body:', errorBody);
            throw new Error(`Failed to fetch users: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Fetched data:', data);

        return {
            totalUsers: data.totalUsers || 0,
            pageNumber: data.pageNumber || 1,
            pageSize: data.pageSize || 10,
            users: data.users || [],
        };
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
};

export const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiration');
    window.location.href = '/login'; // Redirect to login page
};

// Add this API call function at the top of your component or in a separate API file
export const setUserAlwaysWinStatus = async (userId: string, isAlwaysWin: boolean) => {
    try {

        debugger;

        const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Authenticate/SetUserAlwaysWinStatus`,{
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, isAlwaysWin }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to update always win status');
      }
  
      return await response.json();
    } catch (error) {
      console.error('Error setting always win status:', error);
      throw error;
    }
  };

  export const getBalanceByUserId = async (userId: string) => {

    debugger;

    const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Balance/GetBalanceByUserId?userId=${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization token here if required, e.g., 'Authorization': `Bearer ${token}`
      },
    });
    if (!response.ok) {
      throw new Error('Failed to fetch balance');
    }
    return await response.json();
  };

  export const updateBalance = async (
    userId: string,
    availableBalance: number,
    lockingBalance: number,
    isWithdrawal: boolean
  ) => {
    // Note: The original API expects query/form parameters, but for consistency, we'll assume it’s updated to accept JSON body
    const response = await fetch(`${apiBaseUrl.apiBaseUrl}/Balance/UpdateBalance`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization token here if required
      },
      body: JSON.stringify({ userId, availableBalance, lockingBalance, isWithdrawal }),
    });

    debugger;
    if (!response.ok) {
      throw new Error('Failed to update balance');
    }
    return await response;
  };